import { exists } from '@jkhy/frontend-utils/functions';

export function waitFor(
    predicate: () => boolean | Promise<boolean>,
    options?: { timeout?: number; interval?: number; retryOnError?: boolean }
) {
    let iterations = 0;
    let timeoutId: number | undefined = -1;
    const timeout = options?.timeout ?? 2000;
    const interval = options?.interval ?? 50;
    const start = Date.now();
    const createError = (difference: number) =>
        new Error(`waitFor() timed out after ${difference}ms and ${iterations} iterations.`);

    const completed = new Promise<void>((resolve, reject) => {
        const handleResult = (result: boolean, hasTimedOut: boolean, difference: number) => {
            if (result) {
                resolve();
            } else if (hasTimedOut) {
                reject(createError(difference));
            } else {
                timeoutId = window.setTimeout(check, interval);
            }
        };
        const handleError = (err: unknown, hasTimedOut: boolean, difference: number) => {
            if (options?.retryOnError) {
                if (hasTimedOut) {
                    reject(createError(difference));
                } else {
                    timeoutId = window.setTimeout(check, interval);
                }
            } else {
                reject(err);
            }
        };
        const check = () => {
            // if the timeout has been cleared out of band, abort this invocation
            if (!exists(timeoutId)) {
                return;
            }

            iterations++;
            const difference = Date.now() - start;
            const hasTimedOut = difference >= timeout;

            try {
                const result = predicate();
                // handle result synchronously if predicate doesn't return a promise
                if (typeof result === 'boolean') {
                    handleResult(result, hasTimedOut, difference);
                } else {
                    result
                        .then(result => handleResult(result, hasTimedOut, difference))
                        .catch(err => handleError(err, hasTimedOut, difference));
                }
            } catch (err) {
                handleError(err, hasTimedOut, difference);
            }
        };

        check();
    });

    return {
        completed,
        cancel: () => {
            if (!exists(timeoutId)) {
                return;
            }

            // we use -1 to indicate the starting state of the invocation
            if (timeoutId < 0) {
                timeoutId = undefined;
                return;
            }

            window.clearTimeout(timeoutId);
            timeoutId = undefined;
        },
    };
}
